import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { CandidateDetail } from '../components/candidates/candidate-detail.tsx';
import { CandidateForm } from '../components/candidates/candidate-form.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import { describeError } from '../lib/api.ts';
import { api } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { FIELDS_QUERY } from './fields.tsx';

/** The full-page candidate route, `/candidates/$id` (RELAY-944). */
export function CandidatePage() {
  const { id } = useParams({ from: '/authed/candidates/$id' });
  const session = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const customOn = session.isEnabled('custom_fields.enabled');
  const defs = useQuery({
    queryKey: FIELDS_QUERY,
    queryFn: () => api.fieldDefinitions.list('candidates'),
    enabled: customOn,
  });
  const role = session.me.organisation.role;
  const definitions = useMemo(
    () =>
      (customOn ? (defs.data ?? []) : []).filter(
        (d) => (d.visibility[role] ?? 'edit') !== 'hidden',
      ),
    [customOn, defs.data, role],
  );
  const candidate = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => api.candidates.get(id),
  });
  const [editing, setEditing] = useState(false);
  const refetch = async () => {
    await qc.invalidateQueries({ queryKey: ['candidate', id] });
    await qc.invalidateQueries({ queryKey: ['candidate-documents', id] });
    await qc.invalidateQueries({ queryKey: ['candidate-history', id] });
  };

  return (
    <Gate permission="candidate.read" feature="candidates.enabled">
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/candidates' })}>
          <ArrowLeft /> Candidates
        </Button>
      </div>
      {candidate.isPending && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}
      {candidate.error != null && (
        <p className="text-danger text-sm" role="alert">
          {describeError(candidate.error)}
        </p>
      )}
      {candidate.data && (
        <CandidateDetail
          candidate={candidate.data}
          definitions={definitions}
          onEdit={() => setEditing(true)}
          onChanged={refetch}
        />
      )}
      {editing && candidate.data && (
        <CandidateForm
          candidate={candidate.data}
          definitions={definitions}
          role={role}
          onClose={() => setEditing(false)}
          onUploaded={() => void refetch()}
          onSaved={async () => {
            setEditing(false);
            await refetch();
          }}
        />
      )}
    </Gate>
  );
}
